import { MoreHorizontal } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";

import type { FlattenObject } from "@/lib/flatten-obj";
import type { ColumnDef } from "@tanstack/react-table";

import { useOrders } from "@/api/order-query";
import { OrderTab } from "@/components/cards/order-tab";
import { createTableColumns } from "@/components/data-table/columns";
import { DataTable } from "@/components/data-table/data-table";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { flattenObject } from "@/lib/flatten-obj";
import { cn } from "@/lib/utils";
import { Filters } from "@/types/filters";
import { type Order, OrderStatusEnum } from "@/types/order";
import { createFileRoute, Link } from "@tanstack/react-router";

const actionsColumn: ColumnDef<FlatOrder> = {
	id: "actions",
	size: 20,
	enableHiding: false,
	header: () => <div>Actions</div>,
	cell: ({ row }) => {
		const order = row.original;
		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" className="p-0">
						<span className="sr-only">Open menu</span>
						<MoreHorizontal />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem asChild>
						<Link to="/buyer/orders/$orderid" params={{ orderid: order.id }}>View</Link>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	}
};

const customLabels = {
	contract_title: "Contract Title",
	pickupLocation_courier_name: "Pickup Courier",
	pickupLocation_address: "Pickup Address",
	contract_shippingDate: "Shipping Date",
	approvalStatus: "Status",
	orderCode: "Order Code",
	orderAmount: "Price",
	pickupLocation: "Pickup Location",
	dropoffLocation: "Drop Off Location",
	createdAt: "Created At",
	dispatchStatus: "Dispatch Status"
};
type FlatOrder = FlattenObject<Order>;
const columns = createTableColumns<FlatOrder>(
	[
		"contract_title",
		"orderAmount",
		"pickupLocation_courier_name",
		"pickupLocation_address",
		"contract_shippingDate",
		"approvalStatus",
		"createdAt",
		"status",
		"dispatchStatus"
	],
	{
		customLabels
	},
	actionsColumn
);

const BaseParams = z.object({
	title: z.string().optional(),
	status: z.string().optional(),
	endDate: z.string().optional(),
	startDate: z.string().optional(),
	buyerApprovalStatus: z.string().optional(),

	pageIndex: z.number().optional(),
	pageSize: z.number().optional(),
	sortBy: z.string().regex(/^.+\.(asc|desc)$/).optional()
});
export const OrderParams = Filters(BaseParams);

export const Route = createFileRoute("/buyer/orders/")({
	component: OrdersPage,
	validateSearch: OrderParams
});

function OrdersPage() {
	const { data, isPending, isError } = useOrders();

	const { isMobile } = useSidebar();

	const flatData = useMemo(() => {
		return data?.data.map(order => flattenObject(order)) ?? [];
	}, [data]);

	const formattedData = useMemo(() => {
		const paid: Order[] = [];
		const pending: Order[] = [];
		const completed: Order[] = [];
		const rejected: Order[] = [];

		data?.data.forEach((order) => {
			if (order.status === OrderStatusEnum.PENDING) {
				pending.push(order);
			}
			else if (order.status === OrderStatusEnum.COMPLETED) {
				completed.push(order);
			}
			else if (order.status === OrderStatusEnum.PAID) {
				paid.push(order);
			}
			else if (order.status === OrderStatusEnum.REJECTED) {
				rejected.push(order);
			}
		});

		return { pending, completed, rejected, paid };
	}, [data]);

	return (

		<>
			{!isMobile && (
				<DataTable
					data={flatData}
					columns={columns}
					isLoading={isPending}
					isError={isError}
					title="Order History"
				/>
			)}

			<div
				className={cn("w-full mx-auto p-0 space-y-6", {
					hidden: !isMobile
				})}
			>
				<Tabs defaultValue="all" className="w-full bg-white rounded-lg">
					<TabsList className="flex flex-wrap w-full justify-between p-0 border-b rounded-none h-auto bg-transparent">
						<TabsTrigger
							value="all"
							className={cn(
								"rounded-none border-b-2 border-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent",
								"text-sm font-medium px-4 py-2"
							)}
						>
							All
						</TabsTrigger>
						<TabsTrigger
							value="pending"
							className={cn(
								"rounded-none border-b-2 border-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent",
								"text-sm font-medium px-4 py-2"
							)}
						>
							Pending Order
						</TabsTrigger>
						<TabsTrigger
							value="paid"
							className={cn(
								"rounded-none border-b-2 border-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent",
								"text-sm font-medium px-4 py-2"
							)}
						>
							Paid Order
						</TabsTrigger>
						<TabsTrigger
							value="rejected"
							className={cn(
								"rounded-none border-b-2 border-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent",
								"text-sm font-medium px-4 py-2"
							)}
						>
							Rejected Order
						</TabsTrigger>
					</TabsList>
					<OrderTab
						value="all"
						isLoading={isPending}
						orders={data?.data ?? []}
						content={{
							title: "You don't have any any orders yet"
						}}
					/>
					<OrderTab
						value="paid"
						isLoading={isPending}
						orders={formattedData.paid}
						content={{
							title: "You don't have any paid orders"
						}}
					/>
					<OrderTab
						value="completed"
						isLoading={isPending}
						orders={formattedData.completed}
						content={{
							title: "You don't have any completed orders"
						}}
					/>
					<OrderTab
						value="pending"
						isLoading={isPending}
						orders={formattedData.pending}
						content={{
							title: "You don't have any pending orders"
						}}
					/>
					<OrderTab
						value="rejected"
						isLoading={isPending}
						orders={formattedData.rejected}
						content={{
							title: "You don't have any cancelled orders"
						}}
					/>
				</Tabs>
			</div>

		</>
	);
}

// const tabOptions: TabOption[] = [
// 	{ value: "", label: "All" },
// 	{ value: OrderStatusEnum.PAID, label: "Paid" },
// 	{ value: OrderStatusEnum.PENDING, label: "Pending" },
// 	{ value: OrderStatusEnum.CANCELED, label: "Cancelled" }
// ];

// function RouteComponent() {
// 	const { filters, setFilters } = useFilters(Route.id);
// 	const navigate = useNavigate();
// 	const { data, isPending, refetch } = useContracts(filters);

// 	const paginationState = {
// 		pageIndex: filters.pageIndex,
// 		pageSize: filters.pageSize
// 	};
// 	const sortingState = sortByToState(filters.sortBy);

// 	const [activeTab, setActiveTab] = useState<string>(tabOptions[0].value);

// 	const filteredData = useMemo(() => {
// 		if (activeTab === tabOptions[0].value) {
// 			return data?.data;
// 		}
// 		return data?.data.filter(item => ((item.status).toLowerCase()).includes(activeTab.toLowerCase()));
// 	}, [activeTab, data]) ?? [];

// 	useEffect(() => {
// 		refetch();
// 	}, [filters, activeTab]);

// 	return (

// 		<GenericDataTable
// 			columns={columns}
// 			data={filteredData}
// 			activeTab={activeTab}
// 			isLoading={isPending}
// 			tabOptions={tabOptions}
// 			pagination={paginationState}
// 			onTabChange={setActiveTab}
// 			filterField="status"
// 			paginationOptions={{
// 				onPaginationChange: (pagination) => {
// 					setFilters(
// 						typeof pagination === "function"
// 							? pagination(paginationState)
// 							: pagination
// 					);
// 				},
// 				rowCount: data?.totalItems
// 			}}
// 			filters={filters}
// 			handleRowClick={(row) => {
// 				navigate({
// 					to: "/vendor/contract/$contractid",
// 					params: { contractid: row.original.id }
// 				});
// 			}}
// 			onFilterChange={filters => setFilters(filters)}
// 			sorting={sortingState}
// 			onSortingChange={(updaterOrValue) => {
// 				const newSortingState = typeof updaterOrValue === "function"
// 					? updaterOrValue(sortingState)
// 					: updaterOrValue;
// 				return setFilters({ sortBy: stateToSortBy(newSortingState) });
// 			}}
// 		/>
// 	);
// }
